import {Component, DestroyRef, EventEmitter, inject, Output} from '@angular/core';
import {
  ControlContainer,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
} from '@angular/forms';

import {ClientCreationService} from "../services/client-creation.service";
import {AsyncPipe, DatePipe} from "@angular/common";
import {map, startWith, switchMap, tap} from "rxjs/operators";
import {combineLatest, filter, Observable, shareReplay} from "rxjs";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {
  DocumentUploadWrapperComponent,
} from "../../../ibp-components/third-party/ibp-prime-ng/ibp-upload-area/ibp-upload-area.component";
import {documentUpload} from "../../../shared/urls";
import {StateService} from "../../../service/state/state.service";
import {GeneralDataService} from "../../../service/general-data.service";
import {UserProfile} from "../../../models/user/userProfile";
import {UserService} from "../../../service/user.service";
import {
  enumToIbpRadioOption, IbpRadioOption,
} from "../../../ibp-components/third-party/ibp-prime-ng/ibp-radio-button/ibp-radio-button.component";
import {VatMethod2} from "../../../enums/vat-method";
import {
  enumToIbpSelectOptions,
  IbpSelectOption,
} from "../../../ibp-components/third-party/ibp-prime-ng/ibp-select/ibp-select.component";
import {ChartOfAccountsTypes} from "../../../models/chart-accounts/chart-of-accounts-type.enum";
import {LocalesService} from "../../../service/locales.service";
import { IbpConfirmationDialogService } from "../../../ibp-services/third-party/ibp-prime-ng/ibp-confirmation-dialog.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {DomainService} from "../../../service/domain.service";
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';


import { IbpButtonComponent } from '../../../ibp-components/third-party/ibp-prime-ng/ibp-button/ibp-button.component';
import { IbpCheckboxComponent } from '../../../ibp-components/third-party/ibp-prime-ng/ibp-checkbox/ibp-checkbox.component';
import {
  IbpInputTextareaComponent
} from "../../../ibp-components/third-party/ibp-prime-ng/ibp-input-textarea/ibp-input-textarea.component";
@Component({
  selector: 'ibp-summary-step',
  templateUrl: './summary-step.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    TranslatePipe,
    DocumentUploadWrapperComponent,
    DatePipe,
    CommonModule,
    TranslateModule,
    IbpButtonComponent,
    IbpCheckboxComponent,
    IbpInputTextareaComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    }],
})
export class SummaryStepComponent {
  private rootFormGroup: FormGroupDirective = inject(FormGroupDirective);
  private clientCreationService: ClientCreationService = inject(ClientCreationService);
  private translateService: TranslateService = inject(TranslateService);
  protected state = inject(StateService);
  private translate: TranslateService = inject(TranslateService);
  private generalDataService: GeneralDataService = inject(GeneralDataService);
  private userService: UserService = inject(UserService);
  private localesService: LocalesService = inject(LocalesService);
  private confirmationService = inject(IbpConfirmationDialogService);
  private domainService: DomainService = inject(DomainService);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly documentUpload = documentUpload; //todo change path
  public vatMethodRadioOptions: IbpRadioOption[];
  public chartOfAccountTypeOptions: IbpSelectOption[];
  public languageOptions$: Observable<IbpSelectOption[]> = this.loadLanguageOptions();
  public uploadRequired$: Observable<boolean>;

  @Output() prevCallback = new EventEmitter();
  @Output() submitClientCallback = new EventEmitter();
  public form: FormGroup;
  public additionalServices: string;

  countryOptions$ = this.generalDataService.getCountries().pipe(map(countries => countries.map(country => {
    return {label: country.name, value: country.isoCode};
  })));

  protected companyInfoData$ = this.clientCreationService.getCompanyInfoData()
    .pipe();

  protected serviceInfoData$ = this.clientCreationService.getServiceInfoData()
    .pipe(
      tap(value => this.parseToAdditionalServices(value.value))
    );

  protected accountInfoData$ = this.clientCreationService.getAccountInfoData()
    .pipe();

  protected vatMethod$ = this.accountInfoData$
    .pipe(
      tap(() => this.vatMethodRadioOptions = enumToIbpRadioOption(this.translate, VatMethod2, "COMPANY_PROPERTIES.FORM.VAT_METHOD.")),
      map(value => this.vatMethodRadioOptions.find(option => option.value === value.get('accountingInfo.vatMethod').value)?.label)
    )

  protected chartOfAccount$ = this.accountInfoData$
    .pipe(
      tap(() => this.chartOfAccountTypeOptions = enumToIbpSelectOptions(this.translate, ChartOfAccountsTypes, "COMPANY_PROPERTIES.FORM.CHART_OF_ACCOUNT_TYPE_OPTIONS.")),
      map(value => this.chartOfAccountTypeOptions.find(option => option.value === value.get('accountingInfo.chartOfAccountType').value)?.label)
    )

  protected language$ = combineLatest([
    this.accountInfoData$,
    this.languageOptions$
  ])
    .pipe(
      filter(([accountInfoData, languageOptions]) => languageOptions?.length && accountInfoData?.value?.accountingInfo?.language),
      map(([accountInfoData, languageOptions]) => languageOptions.find(option => option.value === accountInfoData.value.accountingInfo.language).label)
    );

  countryName$ = combineLatest([
    this.countryOptions$,
    this.companyInfoData$
  ])
    .pipe(
      filter(([countryOptions, companyInfoData]) => companyInfoData !== null && companyInfoData?.value?.companyInfo?.country),
      map(([countryOptions, companyInfoData]) => {
        return countryOptions.find(value => value.value === companyInfoData.value.companyInfo.country)?.label;
      })
    )

  users$ = this.companyInfoData$
    .pipe(
      map((companyInfoData) => companyInfoData.value.companyInfo.domain),
      filter(value => value !== null && value !== undefined && value !== ''),
      switchMap(value => this.userService.getUsersByDomainId(value)),
      map((users: UserProfile[]) => users.map(user => {
          return {
            label: user.fullName,
            value: user.id
          }
        })
      )
    )

  onlineFiduciary$ = combineLatest([
    this.accountInfoData$,
    this.users$
  ])
  .pipe(
    filter(([accountInfoData, users]) => users && accountInfoData?.value?.accountingInfo?.onlineFiduciary),
    map(([accountInfoData, users]) => users.find(user => user.value === accountInfoData.value.accountingInfo.onlineFiduciary))
  )

  onlineAccountant$ = combineLatest([
    this.accountInfoData$,
    this.users$
  ])
  .pipe(
    filter(([accountInfoData, users]) => users && accountInfoData?.value?.accountingInfo?.onlineAccountant),
    map(([accountInfoData, users]) => users.find(user => user.value === accountInfoData.value.accountingInfo.onlineAccountant))
  )

  constructor() {
    const domains$ = this.domainService.findAllForUser().pipe(shareReplay(1), takeUntilDestroyed(this.#destroyRef));
    this.form = this.rootFormGroup.control;
    this.uploadRequired$ = domains$.pipe(map(domains => !domains.find(domain => domain.id == this.form.get('companyInfo.domain').value).rma));
  }

  public showBusinessPurpose(businessPurpose) {
    const config = {
      message: businessPurpose,
      header: this.translateService.instant('CLIENT_CREATION.COMPANY_INFO.BUSINESS_PURPOSE_HEADER'),
      icon: "pi pi-briefcase",
      acceptVisible: false,
      rejectVisible: false,
    }
    this.confirmationService.confirm(config);
  }

  private parseToAdditionalServices(value: any) {
    if (value.serviceInfo) {
      let additionalServices = [];
      if (value.serviceInfo.documentScanningModule) {
        additionalServices.push(this.translateService.instant('CLIENT_CREATION.SERVICE_INFO.DOCUMENT_SCANNING_MODULE'));
      }
      if (value.serviceInfo.expenseAppModule) {
        additionalServices.push(this.translateService.instant('CLIENT_CREATION.SERVICE_INFO.EXPENSE_MODULE'));
      }
      if (value.serviceInfo.paymentWorkflowModule) {
        additionalServices.push(this.translateService.instant('CLIENT_CREATION.SERVICE_INFO.PAYMENT_WORKFLOW_MODULE'));
      }
      if (value.serviceInfo.refurbishDocuments) {
        additionalServices.push(this.translateService.instant('CLIENT_CREATION.SERVICE_INFO.REFURBISH_DOCUMENTS'));
      }

      this.additionalServices = additionalServices.join(', ')
    } else {
      this.additionalServices = '';
    }
  }

  private loadLanguageOptions() {
    return this.localesService.getSupportedLocales()
      .pipe(
        map(locales =>
          locales.map((locale) => ({ label: locale.name, value: locale.rmaCode }))
        )
      )
  }

  public isLoading(): Observable<boolean> {
    return this.clientCreationService.getClientCreationLoading();
  }

  public onSelectFiles(files: string[]) {
    this.form.get('summaryInfo.files').patchValue(files, {emitEvent: true});
  }

  public isSubmitDisabled(): Observable<boolean> {
    return combineLatest([
      this.isLoading(),
      this.uploadRequired$.pipe(
        map(required => required && (!this.form.get('summaryInfo.files')?.value ||
          this.form.get('summaryInfo.files')?.value.length === 0)),
      ),
      this.form.valueChanges.pipe(startWith(this.form.value))]).pipe(
      map(([isLoading, isRequiredUploadMissing, formValue]) =>
        !formValue.summaryInfo?.isOrderConfirmed || isLoading || isRequiredUploadMissing,
      ),
    );
  }
}
